/**
 * ApiExpandedContent
 *
 * Expanded row body for an API communication-log entry: request/response/PDF
 * attachment openers plus their inlined JSON. Consumer: the CommunicationLog
 * table row expander.
 *
 * Every opener previews in place through `AttachmentPreviewOverlay`, which the
 * JSON payloads reach as its `text` kind (SYD-2375). Downloading is the
 * overlay header's own control, never a side effect of opening. The overlay is
 * mounted unconditionally and driven by `open`, so its exit transition survives.
 * Its overlay is a `communicationLog` host, so Extract schedule there is
 * disabled and points the reader at the form's Attachments.
 *
 * Resolves the attachment refs against the current artifact's own map, through
 * `useCurrentArtifact` — the same route the email row beside it takes, so an API
 * row and an email row in one table never disagree about which document holds
 * the file. An entity record carries attachments as readily as a form version
 * does, and reading the form slot directly left an API row on an entity record
 * claiming it had no payloads. `useFormStore()` stays banned: this mounts inside
 * the log table in the form body, and that hook's spread of `formVersion.data`
 * re-rendered every expanded row on each keystroke in the form (SYD-2252).
 * `useCurrentArtifact` reaches the same leaf without that spread.
 *
 * That live map also carries each file's labels, so nothing here is denormalised
 * and a rename or recolour is immediately correct on an old entry.
 */

import { FC, useCallback, useEffect, useState } from 'react';
import { Typography, Tag, Collapse } from 'antd';
import { ref, getDownloadURL } from 'firebase/storage';
import type { Snapshot } from 'valtio';
import dayjs from 'dayjs';
import {
  ApiCommunicationRecord,
  ArtifactAttachments,
  AttachmentVersion,
  LabelId,
  LabelSet,
} from '#types';
import AttachmentPreviewOverlay from '@/components/rendering/attachments/AttachmentPreviewOverlay';
import { LabelTags } from '@/components/atoms/LabelTags';
import { useAttachmentLabels } from '@/use/useOrganizationParameters';
import { useCurrentArtifact } from '@/use/useCurrentArtifact';
import { storage } from '@/services/startStorage';
import FlexBox from '../../../../atoms/FlexBox';
import FlexCol from '../../../../atoms/FlexCol';
import AttachmentOpener from './AttachmentOpener';
import { apiOutcomeTags } from './apiOutcomeTags';

const { Text, Paragraph } = Typography;

interface ApiExpandedContentProps {
  record: ApiCommunicationRecord;
}

type AttachmentRef = {
  fileUid: string;
  versionId: string;
  fileName: string;
  storagePath: string;
  contentType: string;
  labelIds: readonly LabelId[] | undefined;
};

const resolveAttachment = (
  attachments: ArtifactAttachments | undefined,
  fileUid: string,
): AttachmentRef | undefined => {
  const entry = attachments?.[fileUid];
  if (!entry) return undefined;
  const versionId =
    entry.currentVersionId && entry.versions?.[entry.currentVersionId]
      ? entry.currentVersionId
      : Object.keys(entry.versions ?? {})[0];
  const version: AttachmentVersion | undefined = versionId
    ? entry.versions?.[versionId]
    : undefined;
  if (!versionId || !version?.storagePath) return undefined;
  return {
    fileUid,
    versionId,
    fileName: version.versionFileName ?? entry.fileName ?? 'download',
    storagePath: version.storagePath,
    contentType: version.type,
    labelIds: entry.labelIds,
  };
};

const useFetchedJson = (
  attachment: AttachmentRef | undefined,
): string | undefined => {
  const [text, setText] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!attachment?.storagePath) {
      setText(undefined);
      return;
    }
    let cancelled = false;
    getDownloadURL(ref(storage, attachment.storagePath))
      .then((url) => fetch(url))
      .then((res) => res.text())
      .then((raw) => {
        if (cancelled) return;
        try {
          setText(JSON.stringify(JSON.parse(raw), null, 2));
        } catch {
          setText(raw);
        }
      })
      .catch(() => {
        if (!cancelled) setText(undefined);
      });
    return () => {
      cancelled = true;
    };
  }, [attachment?.storagePath]);

  return text;
};

const JsonPanel: FC<{
  attachment: AttachmentRef | undefined;
  label: string;
  openerLabel: string;
  labelSet: Snapshot<LabelSet>;
  onOpen: (attachment: AttachmentRef) => void;
}> = ({ attachment, label, openerLabel, labelSet, onOpen }) => {
  const text = useFetchedJson(attachment);
  return (
    <FlexCol gap={5}>
      {attachment ? (
        <FlexBox noGrow justifyStart wrap gap={5}>
          <AttachmentOpener
            fileName={attachment.fileName}
            declaredContentType={attachment.contentType}
            openerLabel={openerLabel}
            onOpen={() => onOpen(attachment)}
          />
          <LabelTags set={labelSet} labelIds={attachment.labelIds} />
        </FlexBox>
      ) : (
        <Text type="secondary">No {label} attachment available</Text>
      )}
      {text !== undefined && (
        <Paragraph
          code
          style={{
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            maxHeight: '30vh',
            overflow: 'auto',
            margin: 0,
          }}
        >
          <small>{text}</small>
        </Paragraph>
      )}
    </FlexCol>
  );
};

const ApiExpandedContent: FC<ApiExpandedContentProps> = ({ record }) => {
  const artifact = useCurrentArtifact();
  const attachments =
    artifact.kind === 'none' ? undefined : artifact.data?.attachments;
  const attachmentLabels = useAttachmentLabels();
  const [previewTarget, setPreviewTarget] = useState<AttachmentRef | null>(
    null,
  );

  const closePreview = useCallback(() => {
    setPreviewTarget(null);
  }, []);

  const requestAttachment = record.requestAttachmentFileUid
    ? resolveAttachment(attachments, record.requestAttachmentFileUid)
    : undefined;
  const responseAttachment = record.responseAttachmentFileUid
    ? resolveAttachment(attachments, record.responseAttachmentFileUid)
    : undefined;
  const pdfAttachment = record.submissionPdfFileUid
    ? resolveAttachment(attachments, record.submissionPdfFileUid)
    : undefined;

  const outcomeTag = apiOutcomeTags[record.outcome];

  return (
    <FlexCol
      style={{
        maxHeight: '60vh',
        overflow: 'visible auto',
        width: '100%',
        maxWidth: '100%',
      }}
      gap={5}
    >
      <Text>
        <Text strong>Endpoint: </Text>
        {record.method} {record.endpoint}{' '}
        <Tag color={outcomeTag.color} style={{ margin: 0 }}>
          {outcomeTag.label}
        </Tag>
      </Text>
      <Text>
        <Text strong>Date: </Text>
        {record.timestamp
          ? dayjs(record.timestamp.toDate()).format('YYYY-MM-DD HH:mm')
          : '—'}
      </Text>
      {record.distributorId && (
        <Text>
          <Text strong>Distributor: </Text>
          {record.distributorId}
        </Text>
      )}
      {record.purchaseOrderNumber && (
        <Text>
          <Text strong>PO Number: </Text>
          {record.purchaseOrderNumber}
        </Text>
      )}
      {record.salesdocument && (
        <Text>
          <Text strong>Sales Document: </Text>
          {record.salesdocument}
        </Text>
      )}
      {record.quoteNumber && (
        <Text>
          <Text strong>Quote Number: </Text>
          {record.quoteNumber}
        </Text>
      )}
      {record.statusMessage && (
        <Text>
          <Text strong>Message: </Text>
          {record.statusMessage}
        </Text>
      )}
      {pdfAttachment && (
        <FlexBox noGrow justifyStart wrap gap={5}>
          <Text strong>Submission PDF: </Text>
          <AttachmentOpener
            fileName={pdfAttachment.fileName}
            declaredContentType={pdfAttachment.contentType}
            openerLabel={`Preview submission PDF for log entry ${record.id}`}
            onOpen={() => setPreviewTarget(pdfAttachment)}
          />
          <LabelTags set={attachmentLabels} labelIds={pdfAttachment.labelIds} />
        </FlexBox>
      )}
      <Text>
        <Text strong>URL: </Text>
        <Text code>{record.url}</Text>
      </Text>
      <Collapse
        ghost
        size="small"
        items={[
          {
            key: 'request',
            label: 'Request',
            children: (
              <JsonPanel
                attachment={requestAttachment}
                label="request"
                openerLabel={`Preview request payload for log entry ${record.id}`}
                labelSet={attachmentLabels}
                onOpen={setPreviewTarget}
              />
            ),
          },
          {
            key: 'response',
            label: 'Response',
            children: (
              <JsonPanel
                attachment={responseAttachment}
                label="response"
                openerLabel={`Preview response payload for log entry ${record.id}`}
                labelSet={attachmentLabels}
                onOpen={setPreviewTarget}
              />
            ),
          },
        ]}
      />
      <AttachmentPreviewOverlay
        open={Boolean(previewTarget)}
        extractScheduleHost={{ kind: 'communicationLog' }}
        onClose={closePreview}
        fileUid={previewTarget?.fileUid}
        versionId={previewTarget?.versionId}
        fileName={previewTarget?.fileName}
        declaredContentType={previewTarget?.contentType}
      />
    </FlexCol>
  );
};

export default ApiExpandedContent;
